import { Modal, Form, Select, Input, Radio } from 'antd';
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  CONDITION_OPERATORS,
  ConditionValueType,
  COMPARISON_OPERATOR_OPTIONS,
} from '../utils';

export interface IConditionInputValues {
  operator: string;
  value: string | number | boolean | null;
  valueType: ConditionValueType;
}

export interface IProps {
  open: boolean;
  variablePath: string;
  onConfirm: (values: IConditionInputValues) => void;
  onCancel: () => void;
}

const ConditionInputModal: React.FC<IProps> = ({
  open,
  variablePath,
  onConfirm,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [valueType, setValueType] = useState<ConditionValueType>(
    ConditionValueType.String
  );

  useEffect(() => {
    if (open) {
      form.resetFields();
      setValueType(ConditionValueType.String);
    }
  }, [open, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      let parsedValue: string | number | boolean | null;

      switch (values.valueType) {
        case ConditionValueType.Number:
          parsedValue = Number(values.value);
          break;
        case ConditionValueType.Boolean:
          parsedValue = values.value === 'true';
          break;
        case ConditionValueType.Null:
          parsedValue = null;
          break;
        case ConditionValueType.String:
        default:
          parsedValue = String(values.value);
          break;
      }

      onConfirm({
        operator: values.operator,
        value: parsedValue,
        valueType: values.valueType,
      });

      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleOperatorChange = (operator: string) => {
    // Auto-select appropriate value type based on operator
    if (
      operator === CONDITION_OPERATORS.IS_EMPTY ||
      operator === CONDITION_OPERATORS.IS_NOT_EMPTY
    ) {
      setValueType(ConditionValueType.Null);
      form.setFieldsValue({ valueType: ConditionValueType.Null });
    } else if (
      operator === CONDITION_OPERATORS.GREATER_THAN ||
      operator === CONDITION_OPERATORS.GREATER_THAN_OR_EQUAL ||
      operator === CONDITION_OPERATORS.LESS_THAN ||
      operator === CONDITION_OPERATORS.LESS_THAN_OR_EQUAL
    ) {
      setValueType(ConditionValueType.Number);
      form.setFieldsValue({ valueType: ConditionValueType.Number });
    }
  };

  const isEmptyCheck =
    form.getFieldValue('operator') === CONDITION_OPERATORS.IS_EMPTY ||
    form.getFieldValue('operator') === CONDITION_OPERATORS.IS_NOT_EMPTY;

  return (
    <Modal
      title={`Set Condition for "${variablePath}"`}
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Insert"
      cancelText="Cancel"
      width={500}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          operator: CONDITION_OPERATORS.EQUALS,
          valueType: ConditionValueType.String,
          value: '',
        }}
      >
        <Form.Item
          label="Operator"
          name="operator"
          rules={[{ required: true, message: 'Please select an operator' }]}
        >
          <Select
            placeholder="Select operator"
            options={COMPARISON_OPERATOR_OPTIONS}
            onChange={handleOperatorChange}
          />
        </Form.Item>

        <Form.Item
          label="Value Type"
          name="valueType"
          rules={[{ required: true, message: 'Please select a value type' }]}
        >
          <Radio.Group
            onChange={e => setValueType(e.target.value)}
            disabled={isEmptyCheck}
          >
            <Radio value={ConditionValueType.String}>String</Radio>
            <Radio value={ConditionValueType.Number}>Number</Radio>
            <Radio value={ConditionValueType.Boolean}>Boolean</Radio>
            <Radio value={ConditionValueType.Null}>Null</Radio>
          </Radio.Group>
        </Form.Item>

        {!isEmptyCheck && (
          <Form.Item
            label="Value"
            name="value"
            rules={[
              { required: true, message: 'Please enter a value' },
              {
                validator: (_, value) => {
                  if (valueType === ConditionValueType.Number) {
                    if (isNaN(Number(value))) {
                      return Promise.reject('Please enter a valid number');
                    }
                  }
                  if (valueType === ConditionValueType.Boolean) {
                    if (value !== 'true' && value !== 'false') {
                      return Promise.reject('Please enter true or false');
                    }
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            {valueType === ConditionValueType.Boolean ? (
              <Select
                placeholder="Select boolean value"
                options={[
                  { label: 'true', value: 'true' },
                  { label: 'false', value: 'false' },
                ]}
              />
            ) : (
              <Input
                placeholder={
                  valueType === ConditionValueType.Number
                    ? 'Enter number (e.g., 18)'
                    : 'Enter string value'
                }
                type={
                  valueType === ConditionValueType.Number ? 'number' : 'text'
                }
              />
            )}
          </Form.Item>
        )}

        {isEmptyCheck && (
          <EmptyCheckHint>
            Empty check operators do not require a comparison value
          </EmptyCheckHint>
        )}
      </Form>
    </Modal>
  );
};

export default ConditionInputModal;

const EmptyCheckHint = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  margin-top: -10px;
`;
